import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { Logger, NotificationManager, useApplicationState, useSettings } from '@atlas/core';
import {
  isAvailableMission,
  isCompletedMission,
  isLockedMission,
  SmarticoMissionCard,
} from '@atlas/ui';

import useSignInRedirects from 'components/Auth/useSignInRedirects';
import {
  getSmarticoMissions,
  requestMissionClaimReward,
  startMission,
  useSmartico,
} from 'components/ClientProfile/Achievements/Smartico';
import {
  renderMissionActionButton,
  SMARTICO_PAGE_TAB_KEYS,
  SMARTICO_PAGE_TABS,
  SmarticoMissionsListTabs,
} from 'components/Smartico/smartico-helpers';
import SmarticoMissionsEmptyState from 'components/Smartico/SmarticoMissionsEmptyState';
import SmarticoMissionsLoading from 'components/Smartico/SmarticoMissionsLoading';

const SmarticoMissionsListPage = () => {
  const { authenticated, resolved } = useApplicationState();
  const history = useHistory();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const tabFromUrl = queryParams.get('tab');
  const validTabs = Object.values(SMARTICO_PAGE_TAB_KEYS);

  const initialTab = validTabs.includes(tabFromUrl) ? tabFromUrl : SMARTICO_PAGE_TAB_KEYS.OVERVIEW;

  const [selectedTab, setSelectedTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  const [missions, setMissions] = useState([]);
  const [processingMissionId, setProcessingMissionId] = useState(null);
  const { redirectToSignIn } = useSignInRedirects();

  const { isReadySmartico } = useSmartico();
  const { lang } = useSettings();

  useEffect(() => {
    const currentUrlTab = new URLSearchParams(location.search).get('tab');
    if (validTabs.includes(currentUrlTab) && currentUrlTab !== selectedTab) {
      setSelectedTab(currentUrlTab);
    }
  }, [location.search, selectedTab, validTabs]);

  const onSelectTab = useCallback(
    tabKey => () => {
      setSelectedTab(tabKey);

      const searchParams = new URLSearchParams(location.search);
      searchParams.set('tab', tabKey);

      history.replace({ search: searchParams.toString() });
    },
    [history, location.search],
  );

  const missionsUpdates = useCallback(missionsResponse => {
    Logger.debug('Smartico.missions', missionsResponse);
    setMissions(
      missionsResponse
        .filter(m => isCompletedMission(m) || isLockedMission(m) || isAvailableMission(m))
        .sort((a, b) => {
          const aOrder = Number.isInteger(a.position) ? a.position : 9999;
          const bOrder = Number.isInteger(b.position) ? b.position : 9999;

          return aOrder - bOrder;
        }),
    );
    setLoading(false);
  }, []);

  const loadMissions = useCallback(async () => {
    await getSmarticoMissions({
      cb: missionsUpdates,
      missionsUpdates,
    });
  }, [missionsUpdates]);

  const visibleMissions = useMemo(() => {
    if (loading) return [];

    if (selectedTab === SMARTICO_PAGE_TAB_KEYS.AVAILABLE) {
      return missions.filter(isAvailableMission);
    }

    if (selectedTab === SMARTICO_PAGE_TAB_KEYS.COMPLETED) {
      return missions.filter(isCompletedMission);
    }

    if (selectedTab === SMARTICO_PAGE_TAB_KEYS.LOCKED) {
      return missions.filter(isLockedMission);
    }

    return missions;
  }, [loading, missions, selectedTab]);

  const onClaimMissionReward = useCallback(
    async mission => {
      setProcessingMissionId(mission.id);
      try {
        await requestMissionClaimReward({
          achCompletedId: mission.ach_completed_id,
          missionId: mission.id,
        });
      } catch (e) {
        console.error(e);
      } finally {
        await loadMissions();
        setProcessingMissionId(mission.id);
      }
    },
    [loadMissions],
  );

  const onStartMission = useCallback(
    async missionId => {
      setProcessingMissionId(missionId);
      try {
        await startMission({
          missionId,
        });
      } catch (e) {
        console.error(e);
      } finally {
        await loadMissions();
        setProcessingMissionId(false);
      }
    },
    [loadMissions],
  );

  const onOpenMissionDetails = useCallback(
    async mission => {
      NotificationManager.display({
        data: {
          mission,
          onClaimMissionReward,
          onStartMission,
        },
        type: 'smarticoMissionDetails',
      });
    },
    [onClaimMissionReward, onStartMission],
  );

  useEffect(() => {
    if (!authenticated) {
      setLoading(false);
      return;
    }

    let loadTimer;
    let fallbackTimer;

    if (isReadySmartico) {
      setMissions([]);
      setLoading(true);
      loadTimer = setTimeout(() => {
        loadMissions();
      }, 1000);
    } else {
      fallbackTimer = setTimeout(() => {
        setLoading(false);
      }, 5000);
    }

    return () => {
      clearTimeout(loadTimer);
      clearTimeout(fallbackTimer);
    };
  }, [isReadySmartico, loadMissions, lang, authenticated]);

  if (!resolved || !authenticated) {
    redirectToSignIn();
    return null;
  }

  return (
    <div className="page__main">
      <div className="container-fluid">
        <div className="smartico-missions-list">
          <SmarticoMissionsListTabs
            onSelectTab={onSelectTab}
            selectedTab={selectedTab}
            tabs={SMARTICO_PAGE_TABS}
          />

          {loading && <SmarticoMissionsLoading cardsCount={5} />}

          {!loading && visibleMissions.length === 0 ? (
            <SmarticoMissionsEmptyState />
          ) : (
            <div className="smartico-missions-list__container">
              {visibleMissions.map(mission => (
                <SmarticoMissionCard
                  key={mission.id}
                  mission={mission}
                  onClick={() => onOpenMissionDetails(mission)}
                  renderActionButton={mission =>
                    renderMissionActionButton(mission, {
                      hasDetailsFallback: true,
                      isProcessing: processingMissionId === mission.id,
                      onClaimMissionReward,
                      onOpenMissionDetails,
                      onStartMission,
                    })
                  }
                  showDescription
                  size="medium"
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SmarticoMissionsListPage;
